import {
  BUILDINGS,
  ECONOMY,
  FACTIONS,
  FACTION_ECONOMY,
  PLANET_DEPOSITS,
  TRADE_GOOD_IDS,
  startingBuildings,
  type BuildingCost,
  type BuildingId,
  type FactionId,
  type PlanetKind,
  type SlotLayer,
  type TradeGoodId,
} from '@sd/content';
import { roundMoney } from './market';
import { NO_TECH, type TechMods } from './techs';

/**
 * The economy of a capital (GDD §5), advanced once per economic cycle.
 * Pure functions: every call returns new objects.
 */

export interface Construction {
  readonly targetLevel: number;
  /** The level is finished at the first cycle on or after this tick. */
  readonly readyTick: number;
}

export interface BuildingState {
  readonly id: BuildingId;
  readonly layer: SlotLayer;
  /** Finished level; 0 while the first level is still being built. */
  readonly level: number;
  readonly construction: Construction | null;
}

export type Goods = Readonly<Record<TradeGoodId, number>>;

/** What happened on a planet during the last cycle, for the UI, bots and reports. */
export interface CycleReport {
  readonly population: number;
  readonly populationCap: number;
  readonly jobs: number;
  /** Share of jobs filled, 0..1. */
  readonly staffing: number;
  readonly energyDemand: number;
  readonly solarEnergy: number;
  readonly reactorEnergy: number;
  /** Share of energy demand covered, 0..1. Scales every producer. */
  readonly energyRatio: number;
  /** Share of each need covered this cycle, 0..1. */
  readonly fed: { readonly food: number; readonly electronics: number };
  readonly satisfaction: number;
  readonly outputMultiplier: number;
  readonly produced: Goods;
  readonly consumed: Goods;
  readonly science: number;
  readonly taxes: number;
}

export interface PlanetState {
  readonly planetId: string;
  /** Good the mining complex extracts here. */
  readonly deposit: TradeGoodId;
  /** Starlight at this orbit relative to Earth's (1 = 1 AU from the Sun). */
  readonly solarFlux: number;
  readonly population: number;
  readonly buildings: readonly BuildingState[];
  readonly science: number;
  /** Consecutive cycles without enough food. */
  readonly hungryCycles: number;
  /** Goods produced in each of the last cycles, oldest first (feeds currency rates, GDD §6). */
  readonly outputHistory: readonly Goods[];
  readonly lastCycle: CycleReport | null;
}

const zeroGoods = (): Record<TradeGoodId, number> =>
  Object.fromEntries(TRADE_GOOD_IDS.map((g) => [g, 0])) as Record<TradeGoodId, number>;

/** Goods are kept to a millionth so float noise never blocks selling a whole unit. */
const roundUnits = (value: number): number => Math.round(value * 1e6) / 1e6;

export function createPlanet(
  faction: FactionId,
  planetId: string,
  kind: PlanetKind,
  solarFlux: number,
): PlanetState {
  return {
    planetId,
    deposit: PLANET_DEPOSITS[kind],
    solarFlux,
    population: ECONOMY.startPopulation,
    buildings: startingBuildings(faction).map((b) => ({
      id: b.id,
      layer: b.layer,
      level: 1,
      construction: null,
    })),
    science: 0,
    hungryCycles: 0,
    outputHistory: [],
    lastCycle: null,
  };
}

const capOf = (buildings: readonly BuildingState[]): number =>
  buildings.reduce<number>(
    (cap, b) => cap + BUILDINGS[b.id].populationCap * b.level,
    ECONOMY.basePopulationCap,
  );

export function populationCap(planet: PlanetState): number {
  return capOf(planet.buildings);
}

/** Workers a building needs per level, with technologies (never below 0). */
export const jobsPerLevel = (id: BuildingId, mods: TechMods = NO_TECH): number =>
  Math.max(0, BUILDINGS[id].jobs + (mods.jobsPerLevel[id] ?? 0));

export function slotCount(planet: PlanetState, layer: SlotLayer, mods: TechMods = NO_TECH): number {
  if (layer === 'orbit') return ECONOMY.orbitSlots + mods.orbitSlots;
  // Earned, not grown (#79): the best housing district's level and technologies.
  const { start, max, byHousingLevel } = ECONOMY.surfaceSlots;
  const housing = Math.max(
    0,
    ...planet.buildings.filter((b) => b.id === 'housing').map((b) => b.level),
  );
  const fromHousing = housing > 0 ? (byHousingLevel[housing - 1] ?? 0) : 0;
  return Math.min(max, start + fromHousing + mods.surfaceSlots);
}

export function slotsUsed(planet: PlanetState, layer: SlotLayer): number {
  return planet.buildings.filter((b) => b.layer === layer).length;
}

export function jobCount(planet: PlanetState, mods: TechMods = NO_TECH): number {
  return planet.buildings.reduce((sum, b) => sum + jobsPerLevel(b.id, mods) * b.level, 0);
}

/** Cost of bringing a building to `level` (1 = new building). */
export function buildCost(id: BuildingId, level: number): BuildingCost {
  const factor = ECONOMY.upgradeCostFactor[level as keyof typeof ECONOMY.upgradeCostFactor];
  const base = BUILDINGS[id].cost;
  return {
    cash: roundMoney(base.cash * factor),
    alloys: base.alloys * factor,
    metals: base.metals * factor,
  };
}

export function canAfford(cost: BuildingCost, cash: number, goods: Goods): string | null {
  if (cash < cost.cash) return 'not enough cash';
  if (goods.alloys < cost.alloys) return 'not enough alloys';
  if (goods.metals < cost.metals) return 'not enough metals';
  return null;
}

/** Why a new building cannot go up, or null when it can. Ignores cost. */
export function buildBlocker(
  planet: PlanetState,
  id: BuildingId,
  layer: SlotLayer,
  mods: TechMods = NO_TECH,
): string | null {
  const def = BUILDINGS[id];
  if (!def.layers.includes(layer)) return `${id} cannot be built in ${layer}`;
  if (def.unique && planet.buildings.some((b) => b.id === id)) return 'already built';
  if (slotsUsed(planet, layer) >= slotCount(planet, layer, mods)) return 'no free slot';
  return null;
}

/** Why the building at `index` cannot be upgraded, or null when it can. Ignores cost. */
export function upgradeBlocker(planet: PlanetState, index: number): string | null {
  const b = planet.buildings[index];
  if (!b) return 'no such building';
  if (b.construction) return 'under construction';
  if (b.level >= ECONOMY.maxBuildingLevel) return 'max level';
  return null;
}

export function payCost(cash: number, goods: Goods, cost: BuildingCost) {
  return {
    cash: roundMoney(cash - cost.cash),
    goods: {
      ...goods,
      alloys: roundUnits(goods.alloys - cost.alloys),
      metals: roundUnits(goods.metals - cost.metals),
    },
  };
}

export function startConstruction(
  planet: PlanetState,
  target: { readonly index: number } | { readonly id: BuildingId; readonly layer: SlotLayer },
  readyTick: number,
): PlanetState {
  if ('index' in target) {
    return {
      ...planet,
      buildings: planet.buildings.map((b, i) =>
        i === target.index ? { ...b, construction: { targetLevel: b.level + 1, readyTick } } : b,
      ),
    };
  }
  return {
    ...planet,
    buildings: [
      ...planet.buildings,
      { id: target.id, layer: target.layer, level: 0, construction: { targetLevel: 1, readyTick } },
    ],
  };
}

export interface CycleInput {
  readonly planet: PlanetState;
  readonly faction: FactionId;
  readonly cash: number;
  readonly goods: Goods;
  readonly tick: number;
  /** Energy the player's ships draw from the capital this cycle (fleet upkeep). */
  readonly extraEnergyDemand?: number;
  /** What the player's technologies change (#27). */
  readonly mods?: TechMods;
  /** Share of energy left by a crisis blackout (#29); 1 = none. */
  readonly energyFactor?: number;
}

export interface CycleResult {
  readonly planet: PlanetState;
  readonly cash: number;
  readonly goods: Goods;
  /** Indices of buildings whose construction finished this cycle. */
  readonly completed: readonly number[];
  /** True when hunger made the population shrink this cycle. */
  readonly starved: boolean;
}

/**
 * What the population eats at the start of the next cycle: needs are fed from the
 * stock before the producers run (step 4 below), so this much must be in store.
 */
export function populationNeeds(
  planet: PlanetState,
  faction: FactionId,
): { readonly food: number; readonly electronics: number } {
  return {
    food: planet.population * ECONOMY.foodPerPerson * FACTION_ECONOMY[faction].foodFactor,
    electronics: planet.population * ECONOMY.electronicsPerPerson,
  };
}

/**
 * One economic cycle of a planet, in this order: finish construction, staff
 * the jobs, make energy (solar first, then helium-3 reactors), feed the
 * population, run the producers, collect taxes, grow or shrink.
 */
export function runPlanetCycle({
  planet,
  faction,
  cash,
  goods,
  tick,
  extraEnergyDemand = 0,
  mods = NO_TECH,
  energyFactor = 1,
}: CycleInput): CycleResult {
  const econ = FACTION_ECONOMY[faction];
  const cheapGood = FACTIONS[faction].cheapGood;
  const stock = { ...goods };
  const produced = zeroGoods();
  const consumed = zeroGoods();
  const use = (good: TradeGoodId, amount: number) => {
    stock[good] -= amount;
    consumed[good] += amount;
  };

  // 1. Construction.
  const completed: number[] = [];
  const buildings = planet.buildings.map((b, i) => {
    if (!b.construction || b.construction.readyTick > tick) return b;
    completed.push(i);
    return { ...b, level: b.construction.targetLevel, construction: null };
  });
  const working = buildings.filter((b) => b.level > 0);
  const population = planet.population;

  // 2. Jobs.
  const jobs = working.reduce((sum, b) => sum + jobsPerLevel(b.id, mods) * b.level, 0);
  const staffing = jobs > 0 ? Math.min(1, population / jobs) : 1;
  const employed = Math.min(population, jobs);

  // 3. Energy: free sunlight first; reactors burn helium-3 to cover the rest, up to nominal.
  const nominal = working.reduce(
    (sum, b) => sum + BUILDINGS[b.id].energyOutput * b.level * staffing,
    0,
  );
  const demand =
    working.reduce((sum, b) => sum + BUILDINGS[b.id].energy * b.level, 0) + extraEnergyDemand;
  const solar = nominal * Math.min(ECONOMY.maxSolarFactor, planet.solarFlux);
  const reactorWanted = Math.max(0, Math.min(nominal - solar, demand - solar));
  const heliumPerEnergy = ECONOMY.heliumPerEnergy * mods.heliumPerEnergy;
  const heliumWanted = reactorWanted * heliumPerEnergy;
  const helium = Math.min(heliumWanted, stock.helium3);
  use('helium3', helium);
  // Exact when fully fuelled, so a covered demand is exactly covered.
  const reactor = helium >= heliumWanted ? reactorWanted : helium / heliumPerEnergy;
  const energyRatio = (demand > 0 ? Math.min(1, (solar + reactor) / demand) : 1) * energyFactor;

  // 4. Needs.
  const feed = (good: 'food' | 'electronics', need: number) => {
    const eaten = Math.min(need, stock[good]);
    use(good, eaten);
    return need > 0 ? eaten / need : 1;
  };
  const needs = populationNeeds(planet, faction);
  const fedFood = feed('food', needs.food);
  const fedElectronics = feed('electronics', needs.electronics);
  const satisfaction =
    econ.needWeights.food * fedFood + econ.needWeights.electronics * fedElectronics;
  const { min, max } = ECONOMY.satisfactionOutput;
  const outputMultiplier = min + (max - min) * satisfaction;

  // 5. Producers, in building order; a producer short of inputs runs partly.
  let science = 0;
  for (const b of working) {
    const def = BUILDINGS[b.id];
    let scale = b.level * staffing * energyRatio * outputMultiplier * econ.productionFactor;
    const inputFactor = mods.buildingInputs[b.id] ?? {};
    const inputs = (Object.entries(def.inputs) as [TradeGoodId, number][]).map(
      ([good, amount]) => [good, amount * (inputFactor[good] ?? 1)] as const,
    );
    for (const [good, amount] of inputs) {
      const needed = amount * scale;
      if (needed > stock[good]) scale *= stock[good] / needed;
    }
    if (scale <= 0) continue;
    for (const [good, amount] of inputs) use(good, amount * scale);
    const boost = 1 + (mods.buildingOutput[b.id] ?? 0);
    const out = (good: TradeGoodId, amount: number) => {
      const made = amount * scale * boost * (good === cheapGood ? econ.cheapGoodFactor : 1);
      stock[good] += made;
      produced[good] += made;
    };
    for (const [good, amount] of Object.entries(def.outputs) as [TradeGoodId, number][]) {
      out(good, amount);
    }
    if (def.extracts > 0) out(planet.deposit, def.extracts);
    science += def.science * scale * boost;
  }

  // 6. Taxes.
  const taxes = roundMoney(
    employed * ECONOMY.taxPerEmployed + (population - employed) * ECONOMY.taxPerUnemployed,
  );

  // 7. Growth while fed; after a grace period, hunger shrinks the population.
  const cap = capOf(buildings);
  const wellFed = fedFood >= 1 - 1e-9;
  const hungryCycles = wellFed ? 0 : planet.hungryCycles + 1;
  let nextPopulation = population;
  let starved = false;
  if (wellFed && population < cap) {
    nextPopulation = Math.min(
      cap,
      population +
        Math.max(ECONOMY.minGrowth, ECONOMY.growthRate * population) * (1 + mods.populationGrowth),
    );
  } else if (hungryCycles > ECONOMY.starvationGraceCycles) {
    nextPopulation = Math.max(
      ECONOMY.minPopulation,
      population * (1 - ECONOMY.starvationDeclineRate * (1 - fedFood)),
    );
    starved = nextPopulation < population;
  }

  const round = (g: Record<TradeGoodId, number>) =>
    Object.fromEntries(TRADE_GOOD_IDS.map((id) => [id, roundUnits(g[id])])) as Goods;
  const producedRounded = round(produced);
  const report: CycleReport = {
    population,
    populationCap: cap,
    jobs,
    staffing,
    energyDemand: demand,
    solarEnergy: solar,
    reactorEnergy: reactor,
    energyRatio,
    fed: { food: fedFood, electronics: fedElectronics },
    satisfaction,
    outputMultiplier,
    produced: producedRounded,
    consumed: round(consumed),
    science,
    taxes,
  };

  return {
    planet: {
      ...planet,
      population: roundUnits(nextPopulation),
      buildings,
      science: roundUnits(planet.science + science),
      hungryCycles,
      outputHistory: [...planet.outputHistory, producedRounded].slice(-ECONOMY.outputHistoryCycles),
      lastCycle: report,
    },
    cash: roundMoney(cash + taxes),
    goods: round(stock),
    completed,
    starved,
  };
}
